/**
 * `status` — the host-agnostic **status-editor model**: the pure seam between a combatant's status
 * list and the `apply_status` / `remove_status` commands the encounter room dispatches, following
 * the same discipline as `deploy.ts`/`tracks.ts`.
 *
 * A manual GM-applied status omits the optional `expiry` unless the GM picks one in the add form
 * (the reducer defaults an omitted one to the status's fixed RAW duration if it has one, else
 * `"until_removed"`). The status name is trimmed at the seam so an all-whitespace entry never
 * reaches the reducer; an empty result returns no command.
 *
 * `source` is optional — a GM picking a source combatant in the add form (e.g. the Exemplar
 * applying its own Mark to a target) is the ONLY browser affordance that can stand up a
 * source-scoped, third-party-reaction-eligible status.
 *
 * `partner` is likewise optional — a GM picking a paired combatant in the add form is the ONLY
 * browser affordance that can stand up the "Bondmate" marker `t_bonded:1`'s clauses read
 * (`StatusInstance.partner`).
 */
import type { ApplyStatusCommand, CombatantId, GrantRulingCommand, RemoveStatusCommand, StatusExpiry, StatusInstance } from "@lancer-vtt/domain";
import { type Result } from "@lancer-vtt/shared";

export type ApplyStatusArgs = {
  actorId: string;
  combatantId: CombatantId;
  /** Raw status name from the add form; trimmed at the seam. */
  status: string;
  /**
   * The **applying combatant** this instance's `source` points at — optional, mirroring
   * `ApplyStatusCommand.source`. Omitted on the overwhelming majority of statuses (IMPAIRED,
   * JAMMED, …), which carry no source. Set only when the GM picks a source combatant in the add
   * form (e.g. marking a target with the Exemplar's Mark so a third-party offer has someone to
   * route to).
   */
  source?: CombatantId;
  /**
   * The **paired combatant** this instance's `partner` points at — optional, mirroring
   * `ApplyStatusCommand.partner`. Set only when the GM picks a partner combatant in the add form
   * (e.g. designating a pilot's Bondmate so `t_bonded:1`'s adjacency-gated save/check bonus has a
   * partner to read). Omitted on every status that isn't a paired relationship.
   */
  partner?: CombatantId;
  /**
   * The instance's **expiry descriptor** — optional, mirroring `ApplyStatusCommand.expiry`.
   * Omitted means "the engine default" (the status's fixed RAW duration, else `until_removed`);
   * set when the GM picks one in the add form, e.g. a `save_ends` with the HASE `stat` axis the
   * re-save rolls against (the browser twin of `status add --expiry save_ends --stat <axis>`).
   */
  expiry?: StatusExpiry;
};

/**
 * Build the `apply_status` command for a trimmed status name. Returns the `{ ok:false }` arm
 * for an empty/whitespace name (nothing to apply) so the host can keep the draft and skip the
 * dispatch — the reducer still rejects a duplicate name (the wall), surfaced inline as usual.
 */
export function applyStatusCommand(args: ApplyStatusArgs): Result<ApplyStatusCommand> {
  const status = args.status.trim();
  if (status.length === 0) return { ok: false, error: "status name is empty" };
  return {
    ok: true,
    value: {
      type: "apply_status",
      actorId: args.actorId,
      combatantId: args.combatantId,
      status,
      ...(args.source !== undefined ? { source: args.source } : {}),
      ...(args.partner !== undefined ? { partner: args.partner } : {}),
      ...(args.expiry !== undefined ? { expiry: args.expiry } : {}),
    },
  };
}

export type RemoveStatusArgs = {
  actorId: string;
  combatantId: CombatantId;
  status: string;
};

/** Build the `remove_status` command for a status already on the combatant. */
export function removeStatusCommand(args: RemoveStatusArgs): RemoveStatusCommand {
  return { type: "remove_status", actorId: args.actorId, combatantId: args.combatantId, status: args.status };
}

/** A status's stack count: a stacked GM ruling's `stacks`, else 1. */
export function statusStacks(status: StatusInstance): number {
  return status.ruling?.stacks ?? 1;
}

/**
 * Build the command that takes ONE level off a stacked GM ruling ("Rattled 2" → "Rattled"): a
 * replacing `grant_ruling` carrying the same instance with its count lowered, so it is one undo
 * step like any grant. `undefined` for a status with nothing to decrement (not a ruling, or a
 * single level, which the chip's `×` removes instead).
 */
export function decrementRulingCommand(args: { actorId: string; combatantId: CombatantId; status: StatusInstance }): GrantRulingCommand | undefined {
  const { status } = args;
  const stacks = statusStacks(status);
  if (status.ruling === undefined || stacks < 2) return undefined;
  const { stacks: _stacks, ...rest } = status.ruling;
  return {
    type: "grant_ruling",
    actorId: args.actorId,
    combatantId: args.combatantId,
    status: status.name,
    ruling: stacks - 1 > 1 ? { ...rest, stacks: stacks - 1 } : rest,
    expiry: status.expiry,
    ...(status.appliedTurn !== undefined ? { appliedTurn: status.appliedTurn } : {}),
  };
}

/**
 * The add form's **expiry picks**. `"default"` sends no expiry, so the reducer applies the status's
 * fixed RAW duration (Braced, Power Up) or `until_removed`; the other two name one explicitly. The
 * CLI's wider `--expiry` vocabulary (`turn`/`scene`/`end_of_round`) stays CLI-only: a `turn` expiry
 * minted here would carry no `appliedTurn` stamp.
 */
export type ExpiryPick = "default" | "until_removed" | "save_ends";

/** The HASE axes a `save_ends` re-save can roll against (`ForceSaveAtom.stat`'s vocabulary). */
export const SAVE_STATS = ["hull", "agility", "systems", "engineering"] as const;

/**
 * The {@link StatusExpiry} an expiry pick names, or `undefined` for `"default"`. `stat` rides only
 * a `save_ends`; an empty one leaves a bare `save_ends` (the `+0` re-save fallback), never an
 * invented axis.
 */
export function expiryFromPick(pick: ExpiryPick, stat: string): StatusExpiry | undefined {
  if (pick === "until_removed") return { kind: "until_removed" };
  if (pick === "save_ends") return stat !== "" ? { kind: "save_ends", stat } : { kind: "save_ends" };
  return undefined;
}
